import { useEffect, useMemo, useRef, useState } from "react";
import { FaPlus, FaSearch } from "react-icons/fa";
import { Link, useSearchParams } from "react-router-dom";
import Swal from "sweetalert2";
import { toast } from "react-toastify";
import AdLayout from "../components/AdLayout";
import DataTable from "../components/ui/DataTable";
import UserAdminEditor from "../components/admin/UserAdminEditor";
import Paging from "../components/Paging";
import * as general from "../libs/general";
import { fetchAdminUsersListPermissions } from "../libs/customerCreatePermission";
import axiosInstance from "../axiosInstance";

const mapUserToEditorRow = (u) => ({
  ...u,
  fullName: u.name ?? u.fullName ?? "",
});

const UsersAdminPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [pageData, setPageData] = useState({});
  const [searchQuery, setSearchQuery] = useState("");
  const [itemsPerPage, setItemsPerPage] = useState(50);
  const [isLoading, setIsLoading] = useState(false);
  const [message, setMessage] = useState("");
  const [usersListPerm, setUsersListPerm] = useState({
    loaded: false,
    canView: false,
    canCreate: false,
    canEdit: false,
  });

  const [showEditor, setShowEditor] = useState(false);
  const [editing, setEditing] = useState(null);

  const [viewerIsAdmin, setViewerIsAdmin] = useState(false);
  const [hideAdminAccounts, setHideAdminAccounts] = useState(false);

  const [departments, setDepartments] = useState([]);
  useEffect(() => {
    axiosInstance
      .get("/api/users/departments")
      .then((response) => setDepartments(response.data || []))
      .catch(() => setDepartments([]));
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const p = await fetchAdminUsersListPermissions(axiosInstance);
      if (!cancelled) {
        setUsersListPerm({
          loaded: true,
          canView: p.canView,
          canCreate: p.canCreate,
          canEdit: p.canEdit,
        });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const userDivisionIdList = (u) => {
    if (Array.isArray(u.divisionIds) && u.divisionIds.length) {
      return u.divisionIds.map((x) => Number(x)).filter((n) => !Number.isNaN(n));
    }
    if (u.divisionId != null && u.divisionId !== "") return [Number(u.divisionId)];
    return [];
  };

  /** Nhiều phòng ban / bộ phận; gắn nhãn qua cây departments. */
  const resolveDeptDivisionGroups = (u) => {
    const divIds = userDivisionIdList(u);
    const pairs = divIds.map((divId) => {
      for (const d of departments) {
        const div = (d.divisions || []).find((x) => Number(x.id) === Number(divId));
        if (div) {
          return {
            deptId: d.id,
            deptName: d.name || "",
            divisionId: div.id,
            divisionName: div.name || "",
          };
        }
      }
      return { deptId: null, deptName: "", divisionId: divId, divisionName: "" };
    });
    return { pairs };
  };

  const searchUsers = async (filter = {}) => {
    setIsLoading(true);
    setMessage("");
    const page = filter.page !== undefined ? filter.page : 0;
    const size = filter.size !== undefined ? filter.size : itemsPerPage;
    const keyword = filter.keyword !== undefined ? filter.keyword : searchQuery;
    const excludeAdmin =
      filter.excludeAdmin !== undefined ? filter.excludeAdmin : hideAdminAccounts;
    const viewerForRequest =
      filter.viewerIsAdmin !== undefined ? filter.viewerIsAdmin : viewerIsAdmin;

    if (filter.keyword !== undefined) setSearchQuery(filter.keyword);
    if (filter.size !== undefined) setItemsPerPage(filter.size);

    try {
      const response = await axiosInstance.get("/api/users/searchAdminUsers", {
        params: {
          page,
          size,
          ...(keyword ? { keyword } : {}),
          ...(viewerForRequest && excludeAdmin ? { excludeAdmin: true } : {}),
        },
      });
      setPageData(response.data || {});
    } catch (error) {
      setPageData({});
      setMessage(error.message || "Lỗi tải dữ liệu");
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  };

  const searchUsersRef = useRef(searchUsers);
  searchUsersRef.current = searchUsers;

  useEffect(() => {
    let cancelled = false;
    (async () => {
      let vAdmin = false;
      let hidePref = false;
      try {
        const res = await axiosInstance.get("/api/users/myFlags");
        if (cancelled) return;
        const flags = res?.data?.data;
        vAdmin = flags?.isAdmin === true;
        hidePref = flags?.hideAdminsInAdminUsersList === true;
        setViewerIsAdmin(vAdmin);
        setHideAdminAccounts(hidePref);
      } catch {
        if (!cancelled) {
          setViewerIsAdmin(false);
          setHideAdminAccounts(false);
        }
      }
      if (cancelled) return;
      await searchUsersRef.current({
        page: 0,
        size: itemsPerPage,
        keyword: "",
        excludeAdmin: hidePref,
        viewerIsAdmin: vAdmin,
      });
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- bootstrap: load flags then first page
  }, []);

  const rows = useMemo(() => {
    const list = pageData?.page?.content || [];
    return list.map((u, index) => {
      const groups = resolveDeptDivisionGroups(u);
      return {
        ...u,
        stt: (Number(pageData?.page?.number) || 0) * (Number(pageData?.page?.size) || itemsPerPage) + index + 1,
        fullName: u.name,
        createdAt: u.createdDate,
        divisionPairs: groups.pairs,
      };
    });
  }, [pageData, departments, itemsPerPage]);

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchQuery(value);
    searchUsers({ page: 0, keyword: value });
  };

  const handlePageChange = (pageOneBased) => {
    searchUsers({ page: pageOneBased - 1 });
  };

  const handleItemsPerPageChange = (e) => {
    const size = parseInt(e.target.value, 10);
    searchUsers({ page: 0, size });
  };

  const openCreate = () => {
    if (!usersListPerm.loaded || !usersListPerm.canCreate) {
      toast.warning("Bạn không có quyền thêm user.");
      return;
    }
    if (searchParams.has("userId")) {
      const next = new URLSearchParams(searchParams);
      next.delete("userId");
      setSearchParams(next, { replace: true });
    }
    setEditing(null);
    setShowEditor(true);
  };

  const canOpenUserDetail =
    usersListPerm.loaded && (usersListPerm.canView || usersListPerm.canEdit);

  const openUserDetail = (item) => {
    if (!usersListPerm.loaded || (!usersListPerm.canView && !usersListPerm.canEdit)) {
      toast.warning("Bạn không có quyền xem chi tiết user.");
      return;
    }
    setEditing(mapUserToEditorRow(item));
    setShowEditor(true);
    const id = item?.id != null ? Number(item.id) : NaN;
    if (!Number.isNaN(id) && id > 0) {
      const next = new URLSearchParams(searchParams);
      next.set("userId", String(id));
      setSearchParams(next, { replace: true });
    }
  };

  const closeUserEditor = () => {
    setShowEditor(false);
    setEditing(null);
    if (searchParams.has("userId")) {
      const next = new URLSearchParams(searchParams);
      next.delete("userId");
      setSearchParams(next, { replace: true });
    }
  };

  const requestedUserIdFromUrl = useMemo(() => {
    const raw = searchParams.get("userId");
    if (raw == null || raw === "") return NaN;
    const n = Number(raw);
    return Number.isNaN(n) ? NaN : n;
  }, [searchParams]);

  /** Mở popup từ ?userId= (link từ màn phân quyền, F5, back/forward). */
  useEffect(() => {
    if (!usersListPerm.loaded) return;
    if (Number.isNaN(requestedUserIdFromUrl)) return;
    if (showEditor && Number(editing?.id) === requestedUserIdFromUrl) return;

    let cancelled = false;
    (async () => {
      if (!usersListPerm.canView && !usersListPerm.canEdit) {
        if (!cancelled) {
          toast.warning("Bạn không có quyền xem chi tiết user.");
          const next = new URLSearchParams(searchParams);
          next.delete("userId");
          setSearchParams(next, { replace: true });
        }
        return;
      }

      const id = requestedUserIdFromUrl;
      const inList = (pageData?.page?.content || []).find((u) => Number(u.id) === id);
      let item = inList ? mapUserToEditorRow(inList) : null;
      if (!item) {
        try {
          const res = await axiosInstance.get("/api/users/getUser", { params: { id } });
          const u = res?.data?.data;
          if (cancelled) return;
          if (!u) {
            toast.error("Không tìm thấy user.");
            const next = new URLSearchParams(searchParams);
            next.delete("userId");
            setSearchParams(next, { replace: true });
            return;
          }
          item = mapUserToEditorRow(u);
        } catch {
          if (!cancelled) {
            toast.error("Không tải được chi tiết user.");
            const next = new URLSearchParams(searchParams);
            next.delete("userId");
            setSearchParams(next, { replace: true });
          }
          return;
        }
      }
      if (!cancelled) {
        setEditing(item);
        setShowEditor(true);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- chỉ phản ứng userId URL + quyền đã load
  }, [requestedUserIdFromUrl, usersListPerm.loaded, usersListPerm.canView, usersListPerm.canEdit]);

  const buildUpdatePayload = (model, { promoteExistingId } = {}) => {
    const divIds = Array.isArray(model.divisionIds)
      ? model.divisionIds.map((x) => Number(x)).filter((n) => !Number.isNaN(n))
      : model.divisionId != null && model.divisionId !== ""
        ? [Number(model.divisionId)]
        : [];
    const isNewUser = model.id == null || Number(model.id) === 0;
    return {
      id: promoteExistingId != null ? promoteExistingId : model.id || 0,
      username: model.username || null,
      email: model.email || null,
      name: model.fullName || null,
      phoneNumber: model.phone || null,
      officeAddress: model.address || null,
      divisionIds: divIds,
      divisionId: divIds.length ? divIds[0] : null,
      isAdmin: model.isAdmin === true,
      /** Tạo mới từ màn admin-users → luôn isMember (khác user lần đầu tạo qua đăng nhập Google trên backend). */
      isMember:
        promoteExistingId != null ? true : isNewUser ? true : model.isMember !== false,
    };
  };

  const onSave = async (model) => {
    try {
      const response = await axiosInstance.put("/api/users/update", buildUpdatePayload(model));
      let result = response?.data;
      if (!result?.success && result?.code === "EMAIL_EXISTS_CUSTOMER" && result?.data != null) {
        const confirm = await Swal.fire({
          icon: false,
          title: "Xác nhận",
          text:
            result.message ||
            "Email này đang ở chế độ khách hàng. Bạn có muốn chuyển người dùng của email này trở thành thành viên nội bộ không?",
          showCancelButton: true,
          buttonsStyling: false,
          confirmButtonText: "Chuyển thành thành viên",
          cancelButtonText: "Hủy",
          customClass: {
            confirmButton: "btn btn-primary btn-sm",
            cancelButton: "btn btn-outline-secondary btn-sm",
            actions: "gap-2",
          },
          didOpen: () => {
            const ok = Swal.getConfirmButton();
            const cancel = Swal.getCancelButton();
            if (ok) {
              ok.innerHTML =
                '<i class="fas fa-user-plus me-1" aria-hidden="true"></i> Chuyển thành thành viên';
            }
            if (cancel) {
              cancel.innerHTML = '<i class="fas fa-times me-1" aria-hidden="true"></i> Hủy';
            }
          },
        });
        if (!confirm.isConfirmed) {
          return;
        }
        const retry = await axiosInstance.put(
          "/api/users/update",
          buildUpdatePayload(model, { promoteExistingId: result.data })
        );
        result = retry?.data;
      }
      if (!result?.success) {
        toast.error(result?.message || "Lưu user thất bại.");
        return;
      }
      await searchUsers({
        page: pageData?.page?.number ?? 0,
        size: pageData?.page?.size ?? itemsPerPage,
        keyword: searchQuery,
      });
      closeUserEditor();
      toast.success(result?.message || "Lưu user thành công.");
    } catch (error) {
      const msg = error?.response?.data?.message || "Lưu user thất bại.";
      toast.error(msg);
    }
  };

  const page = pageData?.page;
  const listUsers = page?.content || [];

  return (
    <div>
      <AdLayout>
        <div className="table-responsive web-table customer-management-page">
          <div className="container-fluid">
            <div className="row bg-dark align-items-center">
              <div className="col-12 col-md-7 order-3 order-md-1 py-1 d-flex align-items-center">
                <div className="flex-start gap-2">
                  {usersListPerm.loaded && usersListPerm.canCreate && (
                    <button
                      type="button"
                      className="btn btn-success btn-sm"
                      onClick={openCreate}
                      title="User mới"
                    >
                      <FaPlus /> Thêm mới
                    </button>
                  )}
                </div>
              </div>
              <div className="col-7 col-md-2 py-1 fw-bold text-white text-uppercase d-flex align-items-center justify-content-star order-1 order-md-2">
                Danh sách users
              </div>
              <div className="col-5 col-md-3 py-1 order-2 order-md-3 d-flex justify-content-end align-items-center">
                <form className="form_filter mb-0 w-auto" onSubmit={(e) => e.preventDefault()}>
                  <div className="input-group search-block flex-nowrap">
                    <input
                      name="keyword"
                      type="text"
                      className="form-control form-control-sm"
                      placeholder="Tìm kiếm..."
                      value={searchQuery}
                      onChange={handleSearchChange}
                    />
                    <button
                      type="button"
                      onClick={() => searchUsers({ page: 0, keyword: searchQuery })}
                      className="btn btn-light btn-sm"
                      title="Tìm kiếm..."
                    >
                      <FaSearch />
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>

          <section>
            <DataTable
              maxHeight="calc(100vh - 206px)"
              className="table-normal"
              data-loading={isLoading}
            >
              <table className="table table-striped border-normal table-bordered">
                <thead className="table-primary">
                  <tr>
                    <th className="text-center">STT</th>
                    <th>Tên đăng nhập</th>
                    <th>Họ tên</th>
                    <th>Email</th>
                    <th style={{ width: 130 }} className="text-center">
                      Vai trò
                    </th>
                    <th>Phòng ban - Bộ phận</th>
                    <th style={{ width: 120 }} className="text-center">
                      Ngày tạo
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row) => (
                    <tr key={row.id}>
                      <td className="text-center align-middle">{row.stt}</td>
                      <td className="text-start align-middle">
                        {canOpenUserDetail ? (
                          <button
                            type="button"
                            className="btn btn-link p-0 text-start text-decoration-none"
                            style={{ color: "#485766" }}
                            onClick={() => openUserDetail(row)}
                            title={
                              usersListPerm.canEdit ? "Cập nhật user" : "Xem chi tiết user"
                            }
                          >
                            {row.username || row.email || ""}
                          </button>
                        ) : (
                          <span>{row.username || row.email || ""}</span>
                        )}
                      </td>
                      <td className="text-start align-middle">
                        {canOpenUserDetail ? (
                          <button
                            type="button"
                            className="btn btn-link p-0 text-start text-decoration-none"
                            style={{ color: "#485766" }}
                            onClick={() => openUserDetail(row)}
                            title={
                              usersListPerm.canEdit ? "Cập nhật user" : "Xem chi tiết user"
                            }
                          >
                            {row.fullName || ""}
                          </button>
                        ) : (
                          <span>{row.fullName || ""}</span>
                        )}
                      </td>
                      <td className="text-start align-middle text-break">{row.email || ""}</td>
                      <td className="text-center align-middle">
                        {row.isAdmin ? (
                          <span className="badge bg-danger">Administrator</span>
                        ) : row.isMember ? (
                          <span className="badge bg-primary">Member</span>
                        ) : (
                          ""
                        )}
                      </td>
                      <td className="text-start align-middle">
                        <div className="d-flex flex-wrap gap-1">
                          {row.divisionPairs?.length ? (
                            row.divisionPairs.map((p, i) => {
                              const dn = String(p.deptName ?? "").trim();
                              const dvn = String(p.divisionName ?? "").trim();
                              const label =
                                dn && dvn ? `${dn} > ${dvn}` : dn || dvn || "";
                              if (!label) return null;
                              const inner = (
                                <span className="badge rounded-pill bg-secondary-subtle text-dark border">
                                  {label}
                                </span>
                              );
                              return p.deptId != null && p.divisionId != null ? (
                                <Link
                                  key={`${p.divisionId}-${i}`}
                                  to={`/permission?divisionId=${p.divisionId}`}
                                  className="text-decoration-none"
                                >
                                  {inner}
                                </Link>
                              ) : (
                                <span key={`${p.divisionId}-${i}`}>{inner}</span>
                              );
                            })
                          ) : null}
                        </div>
                      </td>
                      <td className="text-center align-middle">
                        <span title={general.formatDateTime3(row.createdAt)}>
                          {general.formatTimeShort(row.createdAt)}
                        </span>
                      </td>
                    </tr>
                  ))}

                  {message === "" && page && listUsers.length === 0 && (
                    <tr>
                      <td colSpan={7} className="text-center">
                        Không tìm thấy dữ liệu
                      </td>
                    </tr>
                  )}
                  {message !== "" && (
                    <tr>
                      <td colSpan={7} className="text-center">
                        {message}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </DataTable>
          </section>

          <div className="mt-2 d-flex flex-wrap justify-content-between align-items-center gap-2 ps-1">
            {viewerIsAdmin && (
              <div className="form-check mb-0">
                <input
                  id="admin-users-hide-admins"
                  type="checkbox"
                  className="form-check-input"
                  checked={hideAdminAccounts}
                  onChange={async (e) => {
                    const checked = e.target.checked;
                    try {
                      const res = await axiosInstance.get("/api/users/preference/hideAdminsInList", {
                        params: { hideAdmins: checked },
                      });
                      if (!res?.data?.success) {
                        toast.error(res?.data?.message || "Không lưu được cài đặt.");
                        return;
                      }
                      setHideAdminAccounts(checked);
                      const pg = pageData?.page;
                      await searchUsers({
                        page: pg != null && pg.number != null ? pg.number : 0,
                        size: pg?.size != null ? pg.size : itemsPerPage,
                        keyword: searchQuery,
                        excludeAdmin: checked,
                        viewerIsAdmin: true,
                      });
                    } catch (err) {
                      toast.error(err?.response?.data?.message || "Không lưu được cài đặt.");
                    }
                  }}
                />
                <label className="form-check-label" htmlFor="admin-users-hide-admins">
                  Ẩn tài khoản admin
                </label>
              </div>
            )}
            <div className="ms-auto">
              <Paging
                pageData={pageData}
                onPageChange={handlePageChange}
                onItemsPerPageChange={handleItemsPerPageChange}
              />
            </div>
          </div>
        </div>

        <UserAdminEditor
          show={showEditor}
          model={editing}
          readOnly={Boolean(editing?.id) && !usersListPerm.canEdit}
          onClose={closeUserEditor}
          onSave={onSave}
          onAfterDelete={() =>
            searchUsers({
              page: pageData?.page?.number ?? 0,
              size: pageData?.page?.size ?? itemsPerPage,
              keyword: searchQuery,
            })
          }
          onResetPassword={() =>
            searchUsers({
              page: pageData?.page?.number ?? 0,
              size: pageData?.page?.size ?? itemsPerPage,
              keyword: searchQuery,
            })
          }
          existingUsers={listUsers}
        />
      </AdLayout>
    </div>
  );
};

export default UsersAdminPage;
